import { useEffect, useRef, useState } from 'react';
// From the `solid` subpath so this static glyph doesn't drag in the root barrel that
// `useDynamicIcon` loads lazily. See the note in `ui/toast.tsx`.
import { CheckCircle as CheckCircleSolid } from 'iconoir-react/solid';
import { cn } from '@/lib/core/utils/cn';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FavoritesActionToastProps {
  open: boolean;
  message: string;
  onDismiss: () => void;
  /** Time visible before it retreats, in ms. @default 3000 */
  autoDismissMs?: number;
  /**
   * 'bottom' stays flush against the bottom edge, sliding straight up and
   * down. 'center' rises from below the screen to the vertical middle —
   * kept for reference only; every call site now passes 'bottom' after
   * QA flagged the vertically-centered toast as a UI defect.
   * @default 'center'
   */
  anchor?: 'center' | 'bottom';
  actionLabel?: string;
  onAction?: () => void;
}

const EXIT_ANIMATION_MS = 300;

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Rising confirmation banner shared by the Favorites and Shop flows. Used
 * after creating/renaming/deleting a favorites list (anchor="center") and
 * after adding an item to a list from the shop (anchor="bottom").
 */
export function FavoritesActionToast({
  open,
  message,
  onDismiss,
  autoDismissMs = 3000,
  anchor = 'center',
  actionLabel,
  onAction,
}: FavoritesActionToastProps) {
  const [shouldRender, setShouldRender] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setShouldRender(true);
    setIsClosing(false);
    const dismissTimer = window.setTimeout(() => setIsClosing(true), autoDismissMs);
    return () => window.clearTimeout(dismissTimer);
  }, [open, autoDismissMs]);

  // Held in a ref because callers pass an inline arrow: as a dependency it would restart the exit
  // timer on every re-render, so the banner could linger instead of closing.
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!isClosing) return;
    const exitTimer = window.setTimeout(() => {
      setShouldRender(false);
      onDismissRef.current();
    }, EXIT_ANIMATION_MS);
    return () => window.clearTimeout(exitTimer);
  }, [isClosing]);

  if (!shouldRender) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'bg-jncquoi-neutral-1000 fixed inset-x-0 z-90 flex h-17 items-center gap-2 px-4',
        anchor === 'bottom' ? 'bottom-0' : 'toast-anchor-center top-1/2',
        isClosing ? 'animate-toast-slide-out' : 'animate-toast-slide-in'
      )}
    >
      <CheckCircleSolid width={16} height={16} className="text-jncquoi-neutral-00 shrink-0" />
      <p className="font-pp-neue-montreal text-jncquoi-neutral-00 flex-1 text-sm tracking-[0.5px]">
        {message}
      </p>
      {actionLabel && onAction && (
        <button
          type="button"
          onClick={onAction}
          className="font-pp-neue-montreal text-jncquoi-neutral-00 shrink-0 cursor-pointer text-[12px] leading-none font-semibold tracking-[0.5px] uppercase"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}
